import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { CoreComponent } from './core.component';
import { IntroGuard } from '@app/authentication/guards/intro.guard';
import { MilkrunGuard } from '@app/authentication/guards/milkrun.guard';

import { IntroComponent } from '@app/core/layout/intro/intro.component';
import { UserListComponent } from '../roles/admin/pages/users';
import { PlantManagementComponent } from '@app/roles/admin/pages/plants/plants.component';
import { LinesComponent } from '@app/roles/admin/pages/lines/lines.component';
import { RolesComponent } from '@app/roles/admin/pages/roles/roles.component';

import { EquipmentManagementComponent } from '@app/roles/admin/pages/equipment/equipment.component';
import { ToolHistoryComponent } from '../roles/admin/pages/tool-history/tool-history.component';
import { StatusHistoryComponent } from '@app/roles/admin/pages/tool-history/components';
import { LifecycleHistoryComponent } from '@app/roles/admin/pages/tool-history/components';
import { PartNumberManagementComponent } from '@app/roles/admin/pages/partnumbers/partnumber.component';
import { DetailsComponent } from '../tool/details/details.component';
import {
    SpecificToolDetailsComponent,
    SpecificToolDocumentationComponent,
    SpecificToolInterventionsComponent
} from '../tool/details/components';
import { IssuesComponent } from '../roles/admin/pages/issues/issues.component';
import { ReasonsComponent } from '../roles/admin/pages/issues/components/reasons/reasons.component';
import { TicketManagementComponent } from '../roles/admin/pages/ticket-management/tickets.component';
import { OperationTypesComponent } from '@app/roles/admin/pages/ticket-management/components';
import { SignalIssueComponent } from '../roles/line-setter/pages/signal-issue/signal-issue.component';
import { ToolGroupRequestComponent } from '@app/roles/line-setter/pages/tool-request';
import {
    ToolIndividualRequestComponent
} from '@app/roles/line-setter/pages/tool-request';
import { ToolReturnComponent } from '@app/roles/line-setter/pages/tool-return/tool-return.component';
import {
    ToolReturnForMaintenanceComponent
} from '@app/roles/line-setter/pages/tool-return-for-maintenance/tool-return-for-maintenance.component';
import { PendingToolReturnsComponent } from '@app/roles/line-setter/pages/pending-tool-returns/pending-tool-returns.component';
import { ToolRequestComponent } from '@app/roles/line-setter/pages/tool-request';
import { PendingToolRequestsComponent } from '@app/roles/line-setter/pages/pending-tool-requests/pending-tool-requests.component';
import { ToolRequestLinesComponent } from '@app/roles/atom-operator/pages/tool-request-lines/tool-request-lines.component';
import {
    ToolRequestsFacilitiesComponent
} from '@app/roles/atom-operator/pages/tool-requests-facilities/tool-requests-facilities.component';
import { ToolReturnsComponent } from '@app/roles/atom-operator/pages/tool-returns/tool-returns.component';
import { ToolReturnsInspectComponent } from '@app/roles/atom-operator/pages/tool-returns';

import { ProcessToolRequestsFacilitiesComponent } from '@app/roles/atom-operator/pages/tool-requests-facilities/components';
import { ProcessToolRequestComponent } from '@app/roles/atom-operator/pages/tool-request-lines/components';
import { IncomingToolRequestsComponent } from '@app/roles/kardex-operator/pages/incoming-tool-requests/incoming-tool-requests.component';
import { IncomingToolsReturnedComponent } from '@app/roles/kardex-operator/pages/incoming-tools-returned/incoming-tools-returned.component';
import { TransportationRequestsComponent } from '@app/roles/milkrun-operator/pages/transportation-requests';
import { ServiceTicketDetailsComponent, ServiceTicketsComponent } from '@app/roles/service-operator/pages/service-tickets';
import {
    IncomingToolsReturnedDetailComponent
} from '@app/roles/kardex-operator/pages/incoming-tools-returned-detail/incoming-tools-returned-detail.component';
import {
    IncomingToolRequestsDetailComponent
} from '@app/roles/kardex-operator/pages/incoming-tool-requests-detail/incoming-tool-requests-detail.component';
import { ToolReturnsFacilitiesComponent } from '@app/roles/atom-operator/pages/tool-returns-facilities/tool-returns-facilities.component';
import { ToolReturnsFacilitiesInspectComponent } from '@app/roles/atom-operator/pages/tool-returns-facilities/components';
import { Error404Component } from '@app/shared/pages/error404/error404.component';
import { PermissionBasedGuard } from '@app/authentication/guards/permission-based-auth.guard';
import { ToolMaintenancePlanComponent } from "@app/tool/tool-form-modal/tool-maintenance-plan/tool-maintenance-plan.component";
import { DemoComponent } from "@app/demo/demo.component";
import { ProdGuard } from "@app/authentication/guards/prod.guard";
import { AuditsComponent } from "@app/roles/admin/pages/audits-management/audits.component";
import { RequestHistoryComponent } from "@app/roles/admin/pages/request-history/request-history.component";
import { ToolRequestHistoryComponent, ToolReturnHistoryComponent } from "@app/roles/admin/pages/request-history/components";
import { InsertionCyclesComponent } from '@app/roles/admin/pages/insertion-cycles/insertion-cycles.component';
import { ToolUsageComponent } from '@app/roles/admin/pages/tools-usage/tools-usage.component';
import { LiftsComponent, LiftsListComponent } from '@app/lift';
import { LiftDetailsComponent } from '@app/lift/containers/lift-details/lift-details.component';
import { TrayDetailsComponent } from '@app/lift/containers/tray-details/tray-details.component';
import { AuthGuard } from "@app/authentication/guards/auth.guard";
import { ToolListComponent } from "@app/roles/admin/pages/tools";
import { SectionSelectionComponent } from "@app/core/layout/section-selection/section-selection.component";
import { TechnicalProductionBadgeScanComponent } from '@app/core/layout/technical-production-badge-scan/technical-production-badge-scan.component';

import { MaintenanceComponent } from '@app/maintenance/maintenance.component';
import {
    ToolMaintenanceRecurrencesComponent
} from '@app/maintenance/containers/tool-maintenance-recurrences/tool-maintenance-recurrences.component';
import { ToolMaintenancePlansComponent } from '@app/maintenance/containers/tool-maintenance-plans/tool-maintenance-plans.component';
import {
    ToolMaintenanceActivitiesComponent
} from '@app/maintenance/containers/tool-maintenance-activities/tool-maintenance-activities.component';
import { ActivityDetailsComponent } from '@app/maintenance/containers/activity-details/activity-details.component';
import { MaintenancePlanDetailsComponent } from '@app/maintenance/containers/maintenance-plan-details/maintenance-plan-details.component';
import {
    TicketDetailsComponent
} from '@app/roles/service-operator/pages/service-tickets/components/ticket-details/ticket-details.component';
import {
    ServiceTicketTasksComponent
} from '@app/roles/service-operator/pages/service-tickets/components/service-ticket-tasks/service-ticket-tasks.component';
import {
    TicketActivityDetailsComponent
} from '@app/roles/service-operator/pages/service-tickets/components/ticket-activity-details/ticket-activity-details.component';
import {
    ToolStrainMeasurementsComponent
} from "@app/tool/details/components/strain-measurements/tool-strain-measurements/tool-strain-measurements.component";
import { AdminTicketComponent } from '@app/roles/admin/pages/ticket-management/components/admin-ticket/admin-ticket.component';
import { ProcessTypesManagementComponent } from "@app/roles/admin/pages/process-types/process-types.component";
import { ValueStreamManagementComponent } from '@app/roles/admin/pages/value-streams/value-streams.component';

const coreRoutes: Routes = [
    {
        path: '', component: CoreComponent, canActivate: [AuthGuard], children: [
            {
                path: 'dashboard',
                loadChildren: () => import('../dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {path: 'scan-badge', component: TechnicalProductionBadgeScanComponent},
            {path: 'section-selection', component: SectionSelectionComponent},
            {path: '', component: IntroComponent, canActivate: [IntroGuard]},
            {path: 'demo', component: DemoComponent, canActivate: [ProdGuard]},
            {path: 'error404', component: Error404Component},
            {path: 'user-management', component: UserListComponent, canActivate: [PermissionBasedGuard]},
            {path: 'role-management', component: RolesComponent, canActivate: [PermissionBasedGuard]},
            {path: 'plant-management', component: PlantManagementComponent, canActivate: [PermissionBasedGuard]},
            {path: 'value-stream-management', component: ValueStreamManagementComponent, canActivate: [PermissionBasedGuard]},
            {path: 'insertion-cycles', component: InsertionCyclesComponent, canActivate: [PermissionBasedGuard]},
            {
                path: 'lifts', component: LiftsComponent, canActivate: [PermissionBasedGuard], children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        component: LiftsListComponent
                    },

                    {
                        path: ':id', component: LiftDetailsComponent
                    },
                    {path: ':id/trays/:trayId', component: TrayDetailsComponent}
                ]
            },
            {
                path: "strain",
                loadChildren: () => import('../strain/strain.module').then(m => m.StrainModule)
            },
            {
                path: "settings",
                loadChildren: () => import('../settings/settings.module').then(m => m.SettingsModule)
            },
            {path: 'line-management', component: LinesComponent, canActivate: [PermissionBasedGuard]},
            {
                path: 'partnumber-management',
                component: PartNumberManagementComponent,
                canActivate: [PermissionBasedGuard]
            },
            {path: 'tool-management', component: ToolListComponent, canActivate: [PermissionBasedGuard]},
            {
                path: 'tool-management/:id',
                component: DetailsComponent,
                canActivateChild: [PermissionBasedGuard],
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'details'

                    },
                    {path: 'details', component: SpecificToolDetailsComponent},
                    {path: 'documents', component: SpecificToolDocumentationComponent},
                    {path: 'interventions', component: SpecificToolInterventionsComponent},
                    {path: 'maintenance-plan', component: MaintenancePlanDetailsComponent},
                    {path: 'strain-measurements', component: ToolStrainMeasurementsComponent},
                ]
            },
            {path: 'tool-usage', component: ToolUsageComponent, canActivate: [PermissionBasedGuard]},
            {
                path: 'tool-usage/:id',
                component: DetailsComponent,
                canActivateChild: [PermissionBasedGuard],
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'details'

                    },
                    {path: 'details', component: SpecificToolDetailsComponent},
                    {path: 'documents', component: SpecificToolDocumentationComponent},
                    {path: 'interventions', component: SpecificToolInterventionsComponent},
                    {path: 'maintenance-plan', component: ToolMaintenancePlanComponent},
                ]
            },
            {
                path: 'tool-history',
                component: ToolHistoryComponent,
                canActivate: [PermissionBasedGuard],
                children: [
                    {path: '', redirectTo: 'status', pathMatch: 'full'},
                    {path: 'status', component: StatusHistoryComponent},
                    {path: 'lifecycle', component: LifecycleHistoryComponent}
                ]
            },
            {
                path: 'tool-maintenance-plans',
                component: MaintenanceComponent,
                canActivate: [PermissionBasedGuard],
                children: [
                    {path: "", pathMatch: "full", redirectTo: "recurrences"},
                    {path: 'recurrences', component: ToolMaintenanceRecurrencesComponent},
                    {path: 'plans', component: ToolMaintenancePlansComponent},
                    {path: 'activities', component: ToolMaintenanceActivitiesComponent},
                    {path: 'activities/:id/details', component: ActivityDetailsComponent},
                    {path: 'plans/:id/details', component: MaintenancePlanDetailsComponent},
                ]
            },
            {
                path: 'read-tool-maintenance-plans',
                canActivate: [PermissionBasedGuard],
                children: [
                    {path: 'plans/:id/details', component: MaintenancePlanDetailsComponent}
                ]
            },
            {path: 'equipment-management', component: EquipmentManagementComponent, canActivate: [PermissionBasedGuard]},
            {path: 'process-types-management', component: ProcessTypesManagementComponent, canActivate: [PermissionBasedGuard]},
            {path: 'ticket-management', component: TicketManagementComponent, canActivate: [PermissionBasedGuard]},
            {
                path: 'ticket-management/:id',
                component: AdminTicketComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'details'
                    },
                    {path: 'details', component: ServiceTicketDetailsComponent},
                    {path: 'tasks', component: ServiceTicketTasksComponent},
                    {path: 'activities/:activityId/details', component: TicketActivityDetailsComponent}
                ]
            },
            {path: 'operation-types-management', component: OperationTypesComponent, canActivate: [PermissionBasedGuard]},
            {path: 'issues', component: IssuesComponent, canActivate: [PermissionBasedGuard]},
            {path: 'reasons', component: ReasonsComponent, canActivate: [PermissionBasedGuard]},
            {
                path: 'tool-request', canActivate: [PermissionBasedGuard], children: [
                    {
                        path: '',
                        component: ToolRequestComponent,
                        canActivateChild: [PermissionBasedGuard],
                        pathMatch: 'full'
                    },
                    {
                        path: 'individual',
                        component: ToolIndividualRequestComponent
                    },
                    {
                        path: 'group',
                        component: ToolGroupRequestComponent
                    }
                ]
            },
            {
                path: 'signal-issue', canActivate: [PermissionBasedGuard], component: SignalIssueComponent
            },
            {
                path: 'tool-return', canActivate: [PermissionBasedGuard], component: ToolReturnComponent
            },
            {
                path: 'tool-maintenance', canActivate: [PermissionBasedGuard], component: ToolReturnForMaintenanceComponent
            },
            {
                path: 'pending-tool-returns',
                canActivate: [PermissionBasedGuard],
                component: PendingToolReturnsComponent
            },
            {
                path: 'pending-tool-requests',
                canActivate: [PermissionBasedGuard],
                component: PendingToolRequestsComponent
            },
            {
                path: 'tool-requests-lines',
                canActivate: [PermissionBasedGuard],
                component: ToolRequestLinesComponent
            },
            {
                path: 'tool-requests-lines/:id', canActivateChild: [PermissionBasedGuard], children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'process'
                    },
                    {path: 'process', component: ProcessToolRequestComponent},
                ]
            },
            {
                path: 'tool-requests-facilities',
                canActivate: [PermissionBasedGuard],
                component: ToolRequestsFacilitiesComponent
            },
            {
                path: 'tool-requests-facilities/:id', canActivateChild: [PermissionBasedGuard], children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'process'
                    },
                    {path: 'process', component: ProcessToolRequestsFacilitiesComponent},
                ]
            },
            {
                path: 'tool-returns-lines',
                canActivate: [PermissionBasedGuard],
                component: ToolReturnsComponent
            },
            {
                path: 'tool-returns-lines/:id', canActivateChild: [PermissionBasedGuard], children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'inspect'
                    },
                    {path: 'inspect', component: ToolReturnsInspectComponent},
                ]
            },
            {
                path: 'tool-returns-facilities',
                canActivate: [PermissionBasedGuard],
                component: ToolReturnsFacilitiesComponent
            },
            {
                path: 'tool-returns-facilities/:id', canActivateChild: [PermissionBasedGuard], children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'inspect'
                    },
                    {path: 'inspect', component: ToolReturnsFacilitiesInspectComponent},
                ]
            },
            {
                path: 'transportation-requests',
                canActivate: [MilkrunGuard],
                component: TransportationRequestsComponent
            },
            {
                path: 'incoming-tool-requests', canActivate: [PermissionBasedGuard], children: [
                    {
                        path: 'lines',
                        component: IncomingToolRequestsComponent,
                        pathMatch: 'full',
                    },
                    {
                        path: 'facilities',
                        component: IncomingToolRequestsComponent,
                        pathMatch: 'full',
                    },
                    {
                        path: 'lines/:id',
                        component: IncomingToolRequestsDetailComponent,
                        pathMatch: 'full',
                        data: {type: 'lines'}
                    },
                    {
                        path: 'facilities/:id',
                        component: IncomingToolRequestsDetailComponent,
                        pathMatch: 'full',
                        data: {type: 'facilities'}
                    },
                ]
            },
            {
                path: 'incoming-tools-returned', canActivate: [PermissionBasedGuard], children: [
                    {
                        path: 'lines',
                        component: IncomingToolsReturnedComponent,
                        pathMatch: 'full',
                    },
                    {
                        path: 'facilities',
                        component: IncomingToolsReturnedComponent,
                        pathMatch: 'full',
                    },
                    {
                        path: 'lines/:id',
                        component: IncomingToolsReturnedDetailComponent,
                        pathMatch: 'full',
                        data: {type: 'lines'}
                    },
                    {
                        path: 'facilities/:id',
                        component: IncomingToolsReturnedDetailComponent,
                        pathMatch: 'full',
                        data: {type: 'facilities'}
                    }
                ]
            },
            {
                path: 'service-tickets', canActivate: [PermissionBasedGuard], children: [
                    {path: '', component: ServiceTicketsComponent, pathMatch: 'full'},
                ]
            },
            {
                path: 'service-tickets/:id',
                component: TicketDetailsComponent,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'details'
                    },
                    {path: 'details', component: ServiceTicketDetailsComponent},
                    {path: 'tasks', component: ServiceTicketTasksComponent},
                    {path: 'activities/:activityId/details', component: TicketActivityDetailsComponent, pathMatch: 'full'},
                ]
            },
            {
                path: 'audit-management', canActivate: [PermissionBasedGuard], children: [
                    {path: '', component: AuditsComponent, pathMatch: 'full'}
                ]
            },
            {
                path: 'request-history',
                component: RequestHistoryComponent,
                canActivate: [PermissionBasedGuard],
                children: [
                    {path: '', redirectTo: 'toolRequest', pathMatch: 'full'},
                    {path: 'toolRequest', component: ToolRequestHistoryComponent},
                    {path: 'toolReturn', component: ToolReturnHistoryComponent}
                ]
            },
        ]
    }
];

@NgModule({
    imports: [RouterModule.forRoot(coreRoutes, {relativeLinkResolution: 'legacy'})],
    exports: [RouterModule]
})
export class CoreRoutingModule {
}
